import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NavigationConstants } from 'src/app/common/navigation-constants';
import { LoggerService } from 'src/app/telemetry/logger.service';
import { UserStateService } from 'src/app/user-state/user-state.service';
import { VersionUpdateService } from 'src/app/versioning/version-update-service';
import { MenuItem } from '../../menu-item';

/**
 * The component to allow rendering a navigation link.
 */
@Component({
	selector: 'crownx-navbar-link',
	templateUrl: './navbar-link.component.html',
	styleUrls: ['./navbar-link.component.css']
})
export class NavbarLinkComponent {
	/** Represents a single menu item in the menu. */
	@Input() MenuItem!: MenuItem;

	/** Emits an event when an click action is triggered on a menu item. */
	@Output() MenuItemActionTriggered: EventEmitter<void> = new EventEmitter<void>();

	/** Indicates whether a loading operation is in progress. */
	public IsLoadingOperation: boolean = false;

	/**
	 * @constructor
	 *
	 * @param logger The logging service.
	 * @param userStateService The service used to change and get the user state.
	 * @param versionUpdateService The service used for checking and initiating client updates when an update is available
	 */
	constructor(
		private readonly logger: LoggerService,
		private readonly userStateService: UserStateService,
		private readonly versionUpdateService: VersionUpdateService
	) { }

	/**
	 * Tracks when a menu item is clicked and logs an event.
	 * @param telemetryIdentifier The telemetry identifier of the menu item that was clicked.
	 */
	public TrackEvent(telemetryIdentifier: string | null): void {
		if (telemetryIdentifier && !this.userStateService.State.FilterTelemetry) {
			this.logger.TrackEvent(telemetryIdentifier);
		}
	}

	/**
	 * Handles the click action for a specific menu item.
	 * @param menuItem The menu item that was clicked
	 */
	public PerformClickAction(menuItem: MenuItem): void {
		this.TrackEvent(menuItem.TelemetryIdentifier);
		switch (menuItem.Title) {
			case NavigationConstants.UPGRADE_APPLICATION_NAME:
				this.PreformVersionUpdateCheck();
				break;
			default:
				break;
		}
	}

	/**
	 * Checks if a new version update is available and emits an event if no update is found.
	 */
	public async PreformVersionUpdateCheck(): Promise<void> {
		this.IsLoadingOperation = true;
		try {
		const updateAvailable: boolean = await this.versionUpdateService.CheckForUpdatesAndPerformUpdate();
			if (!updateAvailable) {
				this.MenuItemActionTriggered.emit();
			}
		}
		catch (error: any) {
			this.logger.TrackError('Error during manual version update check', error);
		}
		finally {
			this.IsLoadingOperation = false;
		}
	}
}
